import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, AppBar, Toolbar, Avatar, Alert, Chip,
  InputAdornment, MenuItem
} from '@mui/material';
import { ref, onValue, set, remove } from 'firebase/database';
import { database, auth } from '../firebase/Config';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

const Plans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [newPlan, setNewPlan] = useState({
    name: '',
    description: '',
    price: '',
    duration: '30',
    durationType: 'days',
    active: true
  });

  useEffect(() => {
    const plansRef = ref(database, 'plans');
    const unsubscribe = onValue(plansRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const plansList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setPlans(plansList);
      } else {
        setPlans([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan.id);
      setNewPlan({
        name: plan.name,
        description: plan.description,
        price: plan.price,
        duration: plan.duration,
        durationType: plan.durationType,
        active: plan.active
      });
    } else {
      setEditingPlan(null);
      setNewPlan({
        name: '',
        description: '',
        price: '',
        duration: '30',
        durationType: 'days',
        active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlan(null);
  };

  const handleSavePlan = async () => {
    if (!newPlan.name || !newPlan.price || !newPlan.duration) {
      setAlert({ show: true, message: 'Preencha todos os campos obrigatórios!', severity: 'error' });
      return;
    }

    try {
      const planId = editingPlan || `plan_${Date.now()}`;
      const planData = {
        name: newPlan.name,
        description: newPlan.description,
        price: parseFloat(newPlan.price),
        duration: parseInt(newPlan.duration),
        durationType: newPlan.durationType,
        active: newPlan.active,
        createdAt: editingPlan ? plans.find(p => p.id === editingPlan).createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await set(ref(database, `plans/${planId}`), planData);
      
      setAlert({ 
        show: true, 
        message: editingPlan ? 'Plano atualizado com sucesso!' : 'Plano criado com sucesso!', 
        severity: 'success' 
      });
      handleCloseDialog();
    } catch (error) {
      console.error('Erro ao salvar plano:', error);
      setAlert({ show: true, message: 'Erro ao salvar plano!', severity: 'error' });
    }
  };

  const handleDeletePlan = async (planId) => {
    if (window.confirm('Tem certeza que deseja excluir este plano?')) {
      try {
        await remove(ref(database, `plans/${planId}`));
        setAlert({ show: true, message: 'Plano excluído com sucesso!', severity: 'success' });
      } catch (error) {
        console.error('Erro ao excluir plano:', error);
        setAlert({ show: true, message: 'Erro ao excluir plano!', severity: 'error' });
      }
    }
  };

  const getDurationText = (duration, type) => {
    const typeText = {
      days: duration === 1 ? 'dia' : 'dias',
      months: duration === 1 ? 'mês' : 'meses',
      years: duration === 1 ? 'ano' : 'anos'
    };
    return `${duration} ${typeText[type]}`;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        boxShadow: '0 3px 5px 2px rgba(139, 92, 246, .3)'
      }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: '#7c3aed', mr: 2 }}>
            <SubscriptionsIcon />
          </Avatar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gerenciar Planos de Assinatura
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              bgcolor: 'white', 
              color: '#8b5cf6',
              '&:hover': { bgcolor: '#f3e8ff' }
            }}
          >
            Novo Plano
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {alert.show && (
          <Alert 
            severity={alert.severity} 
            onClose={() => setAlert({ ...alert, show: false })}
            sx={{ mb: 2 }}
          >
            {alert.message}
          </Alert>
        )}

        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f3e8ff' }}>
                <TableCell><strong>Nome do Plano</strong></TableCell>
                <TableCell><strong>Descrição</strong></TableCell>
                <TableCell><strong>Valor</strong></TableCell>
                <TableCell><strong>Duração</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      Nenhum plano cadastrado. Clique em "Novo Plano" para começar.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan.id} hover>
                    <TableCell>{plan.name}</TableCell>
                    <TableCell>{plan.description || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                        R$ {plan.price.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getDurationText(plan.duration, plan.durationType)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={plan.active ? 'Ativo' : 'Inativo'}
                        color={plan.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        onClick={() => handleOpenDialog(plan)}
                        sx={{ color: '#8b5cf6' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeletePlan(plan.id)}
                        sx={{ color: '#ef4444' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f3e8ff', color: '#8b5cf6' }}>
          {editingPlan ? 'Editar Plano' : 'Novo Plano'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Nome do Plano *"
            value={newPlan.name}
            onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
            margin="normal"
            placeholder="Ex: Plano Mensal, Plano Anual"
          />
          <TextField
            fullWidth
            label="Descrição"
            value={newPlan.description}
            onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
            placeholder="Descreva os benefícios do plano"
          />
          <TextField
            fullWidth
            label="Valor *"
            type="number"
            value={newPlan.price}
            onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
            margin="normal"
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label="Duração *"
              type="number"
              value={newPlan.duration}
              onChange={(e) => setNewPlan({ ...newPlan, duration: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Tipo *"
              value={newPlan.durationType}
              onChange={(e) => setNewPlan({ ...newPlan, durationType: e.target.value })}
              sx={{ flex: 1 }}
            >
              <MenuItem value="days">Dias</MenuItem>
              <MenuItem value="months">Meses</MenuItem>
              <MenuItem value="years">Anos</MenuItem>
            </TextField>
          </Box>
          <TextField
            select
            fullWidth
            label="Status *"
            value={newPlan.active}
            onChange={(e) => setNewPlan({ ...newPlan, active: e.target.value === 'true' })}
            margin="normal"
          >
            <MenuItem value="true">Ativo</MenuItem>
            <MenuItem value="false">Inativo</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSavePlan} 
            variant="contained"
            sx={{ 
              bgcolor: '#8b5cf6',
              '&:hover': { bgcolor: '#7c3aed' }
            }}
          >
            {editingPlan ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Plans;
